import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getBillingExportRefresh } from 'src/services/billingExportService/getBillingExportRefresh';
import { IBillingExportRefreshView } from 'src/services/billingExportService/types';

/** How often a generating refresh is re-read. Matches the rebill refresh tracker's poll. */
export const BILLING_EXPORT_POLL_MS = 5_000;

const DAY = 'YYYY-MM-DD';

export type TBillingExportRefreshResult = {
  /**
   * The refresh state, or `null` when billing-service gave no clean answer (not deployed yet, a
   * failed read). `null` means "fall back to the pre-BOPS-397 screen", never "failed".
   */
  view: IBillingExportRefreshView | null;
  /** False until the first answer is back, so the button does not flash enabled then disabled. */
  isKnown: boolean;
  /** Re-read now (e.g. the download was refused mid-refresh) and resume polling if generating. */
  recheck: () => Promise<IBillingExportRefreshView | null>;
};

/**
 * The closeout period's billing export refresh state (BOPS-397), polled while it is GENERATING so
 * the download enables on its own when it finishes — no reload.
 *
 * Keyed by the closeout period's own dates, not its month: a month closed in two custom ranges has
 * two billing exports, one per range, each with its own refresh.
 *
 * Once a run is known to be generating, a failed re-read does NOT drop back to the fallback: that
 * would enable the download mid-refresh and hand out exactly the stale file this story removes.
 * The last known GENERATING state is kept and polling continues.
 */
export const useBillingExportRefresh = (
  startDate?: string,
  endDate?: string,
  enabled = true
): TBillingExportRefreshResult => {
  const [view, setView] = useState<IBillingExportRefreshView | null>(null);
  const [isKnown, setIsKnown] = useState<boolean>(false);

  //  `moment(undefined)` is *now* and reports itself valid, so missing dates are rejected first.
  //  Formatted so the effect re-runs on a real change of period, not on a new `location.state`.
  const start = startDate ? moment(startDate) : moment.invalid();
  const end = endDate ? moment(endDate) : moment.invalid();
  const startKey = start.isValid() ? start.format(DAY) : '';
  const endKey = end.isValid() ? end.format(DAY) : '';
  const facilityUuid = localStorage.getItem('facilityId') ?? '';

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  //  Bumped on every period change and unmount, so a read that lands late for an old period is
  //  dropped instead of overwriting the new one.
  const generationRef = useRef(0);
  const viewRef = useRef<IBillingExportRefreshView | null>(null);

  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const read = useCallback(async (): Promise<IBillingExportRefreshView | null> => {
    const generation = generationRef.current;
    const answer = await getBillingExportRefresh({
      facilityUuid,
      start_date: startKey,
      end_date: endKey,
    });
    if (generation !== generationRef.current) return viewRef.current;

    //  See the hook's note: an unclear re-read of a generating refresh keeps it generating.
    const next = answer ?? (viewRef.current?.status === 'GENERATING' ? viewRef.current : null);

    viewRef.current = next;
    setView(next);
    setIsKnown(true);

    clearTimer();
    if (next?.status === 'GENERATING') {
      timerRef.current = setTimeout(() => {
        read();
      }, BILLING_EXPORT_POLL_MS);
    }
    return next;
  }, [facilityUuid, startKey, endKey]);

  useEffect(() => {
    generationRef.current += 1;
    clearTimer();
    viewRef.current = null;
    setView(null);
    setIsKnown(false);

    //  Nothing to ask about: settle on unknown-state fallback rather than sit loading forever.
    if (!enabled || !startKey || !endKey || !facilityUuid) {
      setIsKnown(enabled);
      return undefined;
    }

    read();

    return () => {
      generationRef.current += 1;
      clearTimer();
    };
  }, [enabled, startKey, endKey, facilityUuid, read]);

  return { view, isKnown, recheck: read };
};

export default useBillingExportRefresh;
